<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-list-alt"></i></div>
          <div>
            <div class="panel-title">
              Workstation Logs
              <span class="count-badge" v-if="pagination.total_records > 0">{{ pagination.total_records }}</span>
            </div>
            <div class="panel-sub">Activity log for all workstation enrollments</div>
          </div>
        </div>
        <div class="panel-header-right">
          <button class="btn-refresh" @click="refreshData">
            <i class="fa fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">

        <!-- Search -->
        <div class="search-box">
          <i class="fa fa-search"></i>
          <input type="text" placeholder="Search by TCN…" v-model="search_text" @keypress.enter="search" />
          <button v-if="search_text" class="search-clear" @click="search_text = ''; search()">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Service Provider (admin) -->
        <div class="ts-wrap" v-if="currentUser.u.is_admin" ref="spWrap" v-click-outside="closeSpDropdown">
          <div class="ts-input" @click="toggleSp">
            <span class="ts-val" v-if="!spOpen">{{ spLabel }}</span>
            <input v-if="spOpen" type="text" class="ts-search" v-model="spSearch"
              placeholder="Search provider…" ref="spInput" @keydown.esc="closeSpDropdown" />
            <i class="fa fa-chevron-down ts-arrow" :class="{ open: spOpen }"></i>
          </div>
          <div class="ts-dropdown" v-if="spOpen" :style="spDropStyle">
            <div class="ts-item" @click="selectSp('all')">All Service Providers</div>
            <div class="ts-item" v-for="(c, i) in filteredSp" :key="i" @click="selectSp(c.id)">{{ c.name }}</div>
            <div class="ts-empty" v-if="filteredSp.length === 0 && spSearch">No match</div>
          </div>
        </div>

        <!-- Location (admin) -->
        <div class="ts-wrap" v-if="currentUser.u.is_admin" ref="locWrap" v-click-outside="closeLocDropdown">
          <div class="ts-input" @click="toggleLoc">
            <span class="ts-val" v-if="!locOpen">{{ locLabel }}</span>
            <input v-if="locOpen" type="text" class="ts-search" v-model="locSearch"
              placeholder="Search location…" ref="locInput" @keydown.esc="closeLocDropdown" />
            <i class="fa fa-chevron-down ts-arrow" :class="{ open: locOpen }"></i>
          </div>
          <div class="ts-dropdown" v-if="locOpen" :style="locDropStyle">
            <div class="ts-item" @click="selectLoc('all')">All Locations</div>
            <div class="ts-item" v-for="(c, i) in filteredLoc" :key="i" @click="selectLoc(c.id)">{{ c.name }}</div>
            <div class="ts-empty" v-if="filteredLoc.length === 0 && locSearch">No match</div>
          </div>
        </div>

        <!-- Location (non-admin) -->
        <div class="ts-wrap" v-if="!currentUser.u.is_admin" ref="locWrap" v-click-outside="closeLocDropdown">
          <div class="ts-input" @click="toggleLoc">
            <span class="ts-val" v-if="!locOpen">{{ locNonAdminLabel }}</span>
            <input v-if="locOpen" type="text" class="ts-search" v-model="locSearch"
              placeholder="Search location…" ref="locInput" @keydown.esc="closeLocDropdown" />
            <i class="fa fa-chevron-down ts-arrow" :class="{ open: locOpen }"></i>
          </div>
          <div class="ts-dropdown" v-if="locOpen" :style="locDropStyle">
            <div class="ts-item" @click="selectLocNonAdmin('all')">All Locations</div>
            <div class="ts-item" v-for="(c, i) in filteredLocNonAdmin" :key="i" @click="selectLocNonAdmin(c.id)">{{ c.name }}</div>
            <div class="ts-empty" v-if="filteredLocNonAdmin.length === 0 && locSearch">No match</div>
          </div>
        </div>

        <!-- Workstation -->
        <select class="filter-select" v-model="apikey">
          <option value="all">All Workstations</option>
          <option v-for="(c, i) in getLogsApiKeys" :key="i" :value="c.id">{{ c.label }}</option>
        </select>

        <!-- Dates -->
        <input type="date" class="filter-date" v-model="date_from" @keypress.enter="search" />
        <input type="date" class="filter-date" v-model="date_to" @keypress.enter="search" />

        <button class="btn-search" @click="advSearch">
          <i class="fa fa-search"></i> Search
        </button>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="getLoadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Loading logs…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="apikeylogs.length === 0">
        <i class="fa fa-inbox"></i>
        No log records found
      </div>

      <!-- Table -->
      <div class="table-wrap" v-else>
        <table class="log-table">
          <thead>
            <tr>
              <th style="width:44px">#</th>
              <th style="min-width:140px">Date / Time</th>
              <th style="min-width:140px">Workstation</th>
              <th style="min-width:140px">Location</th>
              <th style="min-width:140px">TCN</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in apikeylogs" :key="index">
              <td class="col-num">{{ (pagination.current_page - 1) * 15 + index + 1 }}</td>
              <td class="col-date">{{ datetimeFormatAmPm(item.log_date) }}</td>
              <td class="col-ws">{{ item.api_key_label }}</td>
              <td class="col-loc">{{ item.client_name }}</td>
              <td class="col-tcn">
                <span>{{ item.tcn || '—' }}</span>
                <button v-if="item.tcn" class="btn-copy" :title="copiedTcn === item.tcn ? 'Copied!' : 'Copy TCN'"
                  @click="copyTcn(item.tcn)">
                  <i :class="copiedTcn === item.tcn ? 'fa fa-check' : 'fa fa-copy'"></i>
                </button>
              </td>
              <td class="col-detail">{{ item.detail }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-bar" v-if="pagination.total_page > 1">
        <span class="pg-info">
          Page {{ pagination.current_page }} of {{ pagination.total_page }}
          · {{ pagination.total_records }} records
        </span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="pagination.current_page === 1" @click="firstPage"><i
              class="fa fa-angle-double-left"></i></button>
          <button class="pg-btn" :disabled="pagination.current_page === 1" @click="previousPage"><i
              class="fa fa-angle-left"></i></button>
          <button v-for="p in visiblePages" :key="p" class="pg-btn" :class="{ active: p === pagination.current_page }"
            @click="selectedPage(p)">{{ p }}</button>
          <button class="pg-btn" :disabled="pagination.current_page === pagination.total_page" @click="nextPage"><i
              class="fa fa-angle-right"></i></button>
          <button class="pg-btn" :disabled="pagination.current_page === pagination.total_page" @click="lastPage"><i
              class="fa fa-angle-double-right"></i></button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import {
  FETCH_CLIENTS,
  FETCH_CLIENT_APIKEYS,
  FETCH_APIKEY_LOGS,
  FETCH_SERVICES_CLIENTS
} from "@/core/services/store/actions.type";
import { SET_APIKEYS, SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";

export default {
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = e => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener("mousedown", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el._clickOutsideHandler);
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getLogsApiKeys", "getSpClients"]),
    filteredSp() {
      const q = (this.spSearch || "").toLowerCase();
      const list = this.getClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    filteredLoc() {
      const q = (this.locSearch || "").toLowerCase();
      const list = this.getSpClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    filteredLocNonAdmin() {
      const q = (this.locSearch || "").toLowerCase();
      const list = this.getClients || [];
      return q ? list.filter(c => c.name && c.name.toLowerCase().includes(q)) : list;
    },
    spLabel() {
      if (this.serviceProviderOutlet === "all") return "All Service Providers";
      const c = (this.getClients || []).find(c => c.id === this.serviceProviderOutlet);
      return c ? c.name : "All Service Providers";
    },
    locLabel() {
      if (this.serviceOutlet === "all") return "All Locations";
      const c = (this.getSpClients || []).find(c => c.id === this.serviceOutlet);
      return c ? c.name : "All Locations";
    },
    locNonAdminLabel() {
      if (this.serviceProviderOutlet === "all") return "All Locations";
      const c = (this.getClients || []).find(c => c.id === this.serviceProviderOutlet);
      return c ? c.name : "All Locations";
    },
    visiblePages() {
      const pages = [];
      const cur = this.pagination.current_page;
      const total = this.pagination.total_page;
      for (let i = Math.max(1, cur - 2); i <= Math.min(total, cur + 2); i++) pages.push(i);
      return pages;
    }
  },
  data() {
    return {
      getLoadingData: false,
      apikey: "all",
      serviceOutlet: "all",
      serviceProviderOutlet: "all",
      spOpen: false, spSearch: "", spDropStyle: {},
      locOpen: false, locSearch: "", locDropStyle: {},
      search_text: "",
      date_from: this.$moment(new Date()).format("YYYY-MM-DD"),
      date_to: this.$moment(new Date()).format("YYYY-MM-DD"),
      apikeylogs: [],
      copiedTcn: null,
      pagination: {
        total_records: 0,
        total_page: 0,
        current_page: 1,
        per_page_data: 15,
        received_per_page_data: 15
      },
      msg: "",
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Workstation Logs", route: "" }
      ]
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    if (this.currentUser.u.is_service_provider) {
      this.$store.dispatch(FETCH_CLIENT_APIKEYS, {
        service_provider: this.serviceProviderOutlet,
        service_location: this.serviceOutlet
      });
    }
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
  },
  methods: {
    datetimeFormatAmPm(date) {
      if (!date) return "—";
      return moment(date).format("MM/DD/YYYY h:mm a");
    },
    copyTcn(tcn) {
      navigator.clipboard.writeText(tcn).then(() => {
        this.copiedTcn = tcn;
        setTimeout(() => { this.copiedTcn = null; }, 1800);
      });
    },
    toggleSp() {
      this.spOpen = !this.spOpen;
      if (this.spOpen) {
        this.spSearch = "";
        this.$nextTick(() => {
          if (this.$refs.spInput) this.$refs.spInput.focus();
          const wrap = this.$refs.spWrap;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            this.spDropStyle = { position: "fixed", top: (r.bottom + 4) + "px", left: r.left + "px", width: r.width + "px", zIndex: 9999 };
          }
        });
      }
    },
    closeSpDropdown() { this.spOpen = false; this.spSearch = ""; this.spDropStyle = {}; },
    selectSp(id) {
      this.serviceProviderOutlet = id;
      this.closeSpDropdown();
      this.loadServicesProvider();
    },
    toggleLoc() {
      this.locOpen = !this.locOpen;
      if (this.locOpen) {
        this.locSearch = "";
        this.$nextTick(() => {
          if (this.$refs.locInput) this.$refs.locInput.focus();
          const wrap = this.$refs.locWrap;
          if (wrap) {
            const r = wrap.getBoundingClientRect();
            this.locDropStyle = { position: "fixed", top: (r.bottom + 4) + "px", left: r.left + "px", width: r.width + "px", zIndex: 9999 };
          }
        });
      }
    },
    closeLocDropdown() { this.locOpen = false; this.locSearch = ""; this.locDropStyle = {}; },
    selectLoc(id) {
      this.serviceOutlet = id;
      this.closeLocDropdown();
      this.loadApiKeys();
    },
    selectLocNonAdmin(id) {
      this.serviceProviderOutlet = id;
      this.closeLocDropdown();
      this.loadApiKeys();
    },
    loadServicesProvider() {
      this.serviceOutlet = "all";
      this.apikey = "all";
      this.$store.commit(SET_APIKEYS, []);
      this.$store.dispatch(FETCH_SERVICES_CLIENTS, this.serviceProviderOutlet);
    },
    loadApiKeys() {
      this.apikey = "all";
      this.$store.dispatch(FETCH_CLIENT_APIKEYS, {
        service_provider: this.serviceProviderOutlet,
        service_location: this.serviceOutlet
      });
    },
    search() {
      this.pagination = { total_records: 0, total_page: 0, current_page: 1, per_page_data: 15, received_per_page_data: 15 };
      this.appData();
    },
    advSearch() { this.search_text = ""; this.search(); },
    refreshData() { this.pagination.current_page = 1; this.appData(); },
    selectedPage(n) { this.pagination.current_page = n; this.appData(); },
    nextPage() { this.pagination.current_page++; this.appData(); },
    lastPage() { this.pagination.current_page = this.pagination.total_page; this.appData(); },
    firstPage() { this.pagination.current_page = 1; this.appData(); },
    previousPage() { this.pagination.current_page--; this.appData(); },
    appData() {
      this.apikeylogs = [];
      this.getLoadingData = true;
      this.$store.dispatch(FETCH_APIKEY_LOGS, {
        service_outlet: this.serviceOutlet,
        date_from: this.date_from,
        date_to: this.date_to,
        api_key_id: this.apikey,
        search_text: this.search_text,
        service_provider: this.serviceProviderOutlet,
        page: this.pagination.current_page
      })
        .then(res => {
          this.getLoadingData = false;
          this.apikeylogs = res.data;
          this.pagination.total_records = res.total_record;
          this.pagination.total_page = res.page_range;
          this.pagination.received_per_page_data = res.data.length;
        })
        .catch(err => { this.getLoadingData = false; this.msg = err.error; });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

/* Panel */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  gap: 10px;
  flex-wrap: wrap;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  font-size: 11px;
  font-weight: 600;
  color: #7a8499;
  background: #eef0f6;
  border-radius: 10px;
  padding: 1px 7px;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 2px;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 7px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: $blue;
    color: $blue;
  }
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f3fa;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 160px;
  max-width: 260px;

  i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aab8;
    font-size: 12px;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 36px;
    border: 1.5px solid $border;
    border-radius: 8px;
    padding: 0 30px 0 30px;
    font-size: 13px;
    font-family: $font;
    color: $dark;
    outline: none;
    background: #fff;
    transition: border-color 0.2s;

    &::placeholder {
      color: #b0bac9;
    }

    &:focus {
      border-color: $blue;
      box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
    }
  }
}

.search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #a0aab8;
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover {
    color: $dark;
  }
}

/* ── Type-and-select ── */
.ts-wrap {
  position: relative;
  min-width: 160px;
  max-width: 200px;
}

.ts-input {
  display: flex;
  align-items: center;
  height: 36px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  cursor: pointer;
  gap: 6px;
  transition: border-color 0.2s;

  &:hover { border-color: $blue; }
  &:focus-within {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
  }
}

.ts-val {
  flex: 1;
  font-size: 12px;
  color: $dark;
  font-family: $font;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;
}

.ts-search {
  flex: 1;
  border: none;
  outline: none;
  font-size: 12px;
  color: $dark;
  font-family: $font;
  background: transparent;
  min-width: 0;

  &::placeholder { color: #b0bac9; }
}

.ts-arrow {
  font-size: 10px;
  color: #a0aab8;
  flex-shrink: 0;
  transition: transform 0.2s;
  pointer-events: none;

  &.open { transform: rotate(180deg); }
}

.ts-dropdown {
  position: fixed;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(13, 27, 62, 0.12);
  z-index: 9999;
  max-height: 210px;
  overflow-y: auto;
}

.ts-item {
  padding: 8px 12px;
  font-size: 12px;
  color: $dark;
  font-family: $font;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  transition: background 0.12s;

  &:hover { background: #f0f7fd; color: $blue; }
}

.ts-empty {
  padding: 10px 12px;
  font-size: 12px;
  color: #a0aab8;
  text-align: center;
}

.filter-select {
  height: 36px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 28px 0 10px;
  font-size: 12px;
  font-family: $font;
  color: $dark;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23a0aab8'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
  outline: none;
  cursor: pointer;
  max-width: 180px;
  transition: border-color 0.2s;

  &:focus { border-color: $blue; }
}

.filter-date {
  height: 36px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 12px;
  font-family: $font;
  color: $dark;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: $blue;
  }
}

.btn-search {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  background: $blue;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: opacity 0.2s;

  i {
    font-size: 11px;
  }

  &:hover {
    opacity: 0.9;
  }
}

/* States */
.state-box {
  text-align: center;
  padding: 48px 24px;
  color: #a0aab8;
  font-size: 13px;

  i {
    font-size: 28px;
    display: block;
    margin-bottom: 10px;
    opacity: 0.45;
  }
}

/* Table */
.table-wrap {
  overflow-x: auto;
}

.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: auto;

  thead tr {
    background: #fff;
    border-bottom: 2px solid #eef0f8;

    th {
      padding: 9px 12px;
      font-size: 11px;
      font-weight: 700;
      color: #7a8499;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
      text-align: left;
    }
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child {
      border-bottom: none;
    }

    &:hover td {
      background: #f8fbff;
    }

    td {
      padding: 10px 12px;
      color: $dark;
      vertical-align: middle;
    }
  }
}

.col-num {
  color: #a0aab8;
  font-size: 12px;
}

.col-date {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.col-ws {
  font-weight: 500;
  font-size: 12px;
}

.col-loc {
  font-size: 12px;
  color: #4a5568;
}

.col-tcn {
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  white-space: nowrap;
}

.col-detail {
  font-size: 12px;
  color: #4a5568;
}

.btn-copy {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 1px solid $border;
  border-radius: 4px;
  background: #f7f9fc;
  color: #a0aab8;
  font-size: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s;

  &:hover {
    border-color: $blue;
    color: $blue;
    background: #eef2fb;
  }

  .fa-check {
    color: #276749;
  }
}

/* Pagination */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f0f3fa;
  flex-wrap: wrap;
  gap: 10px;
}

.pg-info {
  font-size: 12px;
  color: #7a8499;
}

.pg-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pg-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1.5px solid $border;
  background: #fff;
  color: #4a5568;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    border-color: $blue;
    color: $blue;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &.active {
    background: $blue;
    border-color: $blue;
    color: #fff;
  }
}
</style>
